import {
  useEffect,
  useId,
  useRef,
  useState,
  type ClipboardEvent as ReactClipboardEvent,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import {
  FormatBoldOutlined,
  FormatItalicOutlined,
  FormatListBulletedOutlined,
  FormatQuoteOutlined,
  LinkOffOutlined,
  LinkOutlined,
  RedoOutlined,
  TitleOutlined,
  UndoOutlined,
} from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Link as MuiLink, Stack, Tooltip, Typography, type BoxProps } from '@mui/material';
import { AnchoredPopover, Field, FormTextField, createFocusRingStyles, createInputSurfaceStyles, designTokens } from '../../design-system';
import {
  escapeWorkflowRichTextLinkTarget,
  escapeWorkflowRichTextMarkdown,
  normalizeWorkflowRichTextLinkTarget,
  parseWorkflowRichTextMarkdown,
  workflowRichTextHasContent,
  type WorkflowRichTextBlock,
  type WorkflowRichTextInlineNode,
} from './WorkflowRichTextContract';

export { workflowRichTextHasContent } from './WorkflowRichTextContract';

type FormatState = {
  bold: boolean;
  italic: boolean;
  heading: boolean;
  list: boolean;
  quote: boolean;
  link: boolean;
  linkable: boolean;
};

const emptyFormatState: FormatState = { bold: false, italic: false, heading: false, list: false, quote: false, link: false, linkable: false };
const zeroWidthSpace = '\u200B';
const blockTags = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'LI']);

// 편집기 경계 안에 있는 도구는 모바일에서도 초점 표시가 잘리지 않게 안쪽에 그립니다.
const toolbarIconButtonSx = {
  borderRadius: designTokens.shape.sm,
  color: designTokens.color.secondaryText,
  '&.Mui-focusVisible, &:focus-visible': { ...createFocusRingStyles({ inset: true }), boxShadow: 'none' },
} as const;

export type WorkflowRichTextEditorProps = {
  id?: string;
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  placeholder?: string;
  ariaLabel?: string;
};

export type WorkflowRichTextViewerProps = {
  value: string;
  emptyText?: ReactNode;
  sx?: BoxProps['sx'];
};

/**
 * 제한형 Markdown을 React text node로만 렌더링한다. 전달된 HTML은 markup으로 실행하지 않고
 * 그대로 문자로 표시한다.
 */
export function WorkflowRichTextViewer({ value, emptyText, sx }: WorkflowRichTextViewerProps) {
  const blocks = parseWorkflowRichTextMarkdown(value);
  if (!workflowRichTextHasContent(value)) {
    return emptyText === undefined ? null : <Typography variant="body2" sx={{ color: designTokens.color.secondaryText }}>{emptyText}</Typography>;
  }

  return <Box sx={sx}>{blocks.map((block, index) => renderBlock(block, index))}</Box>;
}

/**
 * Workflow LONG_TEXT 문자열을 제한형 Markdown으로 유지하는 문서형 editor다. editor DOM은
 * 안전한 React/DOM node로만 만들며 rich paste도 허용 목록을 통과한 구조만 보존한다.
 */
export function WorkflowRichTextEditor({
  id,
  label,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  required = false,
  error = false,
  helperText,
  placeholder = '문서 내용을 작성하세요.',
  ariaLabel,
}: WorkflowRichTextEditorProps) {
  const generatedId = useId();
  const controlId = id ?? `workflow-rich-text-${generatedId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const linkTargetId = `${controlId}-link-target`;
  const editorRef = useRef<HTMLDivElement>(null);
  const renderedValueRef = useRef<string | undefined>(undefined);
  const savedRangeRef = useRef<Range | undefined>(undefined);
  const historyRef = useRef<{ past: string[]; future: string[] }>({ past: [], future: [] });
  const linkButtonRef = useRef<HTMLButtonElement>(null);
  const [, setHistoryRevision] = useState(0);
  const [formats, setFormats] = useState<FormatState>(emptyFormatState);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState('');
  const [linkError, setLinkError] = useState('');
  const locked = disabled || readOnly;
  const toolbarLabel = ariaLabel ?? (typeof label === 'string' ? `${label} 서식 도구` : '문서 내용 서식 도구');

  const refreshHistoryControls = () => setHistoryRevision((current) => current + 1);

  const renderEditorValue = (nextValue: string, focus = false) => {
    const editor = editorRef.current;
    if (!editor) return;
    renderMarkdownInto(editor, nextValue);
    renderedValueRef.current = nextValue;
    savedRangeRef.current = undefined;
    if (focus) placeCaretAtEnd(editor);
  };

  const emitEditorValue = (nextValue: string, recordHistory = true) => {
    const previousValue = renderedValueRef.current ?? value;
    if (nextValue === previousValue) return;
    if (recordHistory) {
      const history = historyRef.current;
      history.past.push(previousValue);
      if (history.past.length > 100) history.past.shift();
      history.future = [];
    }
    renderedValueRef.current = nextValue;
    onChange(nextValue);
    refreshHistoryControls();
  };

  const commitEditorDom = () => {
    const editor = editorRef.current;
    if (!editor) return;
    emitEditorValue(serializeEditor(editor));
  };

  const refreshFormats = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = editor.ownerDocument.getSelection();
    if (!selection?.rangeCount || !selectionInside(editor, selection)) {
      setFormats(emptyFormatState);
      return;
    }
    const range = selection.getRangeAt(0);
    savedRangeRef.current = range.cloneRange();
    const element = closestElement(range.commonAncestorContainer, editor);
    const link = element?.closest('a');
    const startBlock = closestBlock(range.startContainer, editor);
    const endBlock = closestBlock(range.endContainer, editor);
    setFormats({
      bold: Boolean(element?.closest('strong, b')),
      italic: Boolean(element?.closest('em, i')),
      heading: Boolean(element?.closest('h1, h2, h3')),
      list: Boolean(element?.closest('li, ul, ol')),
      quote: Boolean(element?.closest('blockquote')),
      link: Boolean(link && editor.contains(link)),
      linkable: !range.collapsed && Boolean(range.toString().trim()) && (!startBlock || !endBlock || startBlock === endBlock),
    });
  };

  useEffect(() => {
    if (renderedValueRef.current === value) return;
    renderEditorValue(value);
    historyRef.current = { past: [], future: [] };
    refreshHistoryControls();
  }, [value]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return undefined;
    const ownerDocument = editor.ownerDocument;
    const handleSelectionChange = () => refreshFormats();
    ownerDocument.addEventListener('selectionchange', handleSelectionChange);
    return () => ownerDocument.removeEventListener('selectionchange', handleSelectionChange);
  });

  useEffect(() => {
    if (!linkPopoverOpen) return undefined;
    const ownerDocument = editorRef.current?.ownerDocument;
    const ownerWindow = ownerDocument?.defaultView;
    if (!ownerDocument || !ownerWindow) return undefined;
    const timeout = ownerWindow.setTimeout(() => ownerDocument.getElementById(linkTargetId)?.focus(), 0);
    return () => ownerWindow.clearTimeout(timeout);
  }, [linkPopoverOpen, linkTargetId]);

  const currentRange = () => {
    const editor = editorRef.current;
    if (!editor) return undefined;
    const selection = editor.ownerDocument.getSelection();
    if (selection?.rangeCount && selectionInside(editor, selection)) return selection.getRangeAt(0);
    const savedRange = savedRangeRef.current;
    if (savedRange && editor.contains(savedRange.commonAncestorContainer)) return savedRange;
    let target = editor.lastElementChild;
    if (!target) {
      target = editor.ownerDocument.createElement('p');
      target.append(editor.ownerDocument.createElement('br'));
      editor.append(target);
    }
    const range = editor.ownerDocument.createRange();
    range.selectNodeContents(target);
    range.collapse(false);
    return range;
  };

  const restoreRange = (range: Range) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = editor.ownerDocument.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    savedRangeRef.current = range.cloneRange();
    editor.focus();
    refreshFormats();
  };

  const closeLinkPopover = () => {
    setLinkPopoverOpen(false);
    setLinkError('');
  };

  const openLinkPopover = () => {
    if (locked) return;
    const editor = editorRef.current;
    const range = currentRange();
    if (!editor || !range) return;
    const existing = closestElement(range.commonAncestorContainer, editor)?.closest('a');
    const startBlock = closestBlock(range.startContainer, editor);
    const endBlock = closestBlock(range.endContainer, editor);
    if ((!existing || !editor.contains(existing)) && (range.collapsed || !range.toString().trim() || (startBlock && endBlock && startBlock !== endBlock))) return;
    savedRangeRef.current = range.cloneRange();
    setLinkTarget(existing && editor.contains(existing) ? existing.getAttribute('href') ?? '' : '');
    setLinkError('');
    setLinkPopoverOpen(true);
  };

  const applyLink = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (locked) return;
    const safeTarget = normalizeWorkflowRichTextLinkTarget(linkTarget);
    if (!safeTarget) {
      setLinkError('http://, https:// 또는 mailto:로 시작하는 올바른 주소를 입력하세요.');
      return;
    }
    const editor = editorRef.current;
    const range = currentRange();
    if (!editor || !range) return;
    const existing = closestElement(range.commonAncestorContainer, editor)?.closest('a');
    let link: HTMLAnchorElement;
    if (existing instanceof HTMLAnchorElement && editor.contains(existing)) {
      link = existing;
      link.setAttribute('href', safeTarget);
    } else {
      if (range.collapsed || !range.toString().trim()) {
        setLinkError('링크로 만들 텍스트를 먼저 선택하세요.');
        return;
      }
      link = editor.ownerDocument.createElement('a');
      link.setAttribute('href', safeTarget);
      link.append(range.extractContents());
      range.insertNode(link);
    }
    const linkedRange = editor.ownerDocument.createRange();
    linkedRange.selectNodeContents(link);
    closeLinkPopover();
    restoreRange(linkedRange);
    commitEditorDom();
  };

  const removeLink = () => {
    if (locked) return;
    const editor = editorRef.current;
    const range = currentRange();
    if (!editor || !range) return;
    const existing = closestElement(range.commonAncestorContainer, editor)?.closest('a');
    if (!existing || !editor.contains(existing)) return;
    const unwrappedRange = unwrapElement(existing);
    restoreRange(unwrappedRange);
    commitEditorDom();
  };

  const toggleInline = (tagName: 'strong' | 'em') => {
    if (locked) return;
    const editor = editorRef.current;
    const range = currentRange();
    if (!editor || !range) return;
    const startBlock = closestBlock(range.startContainer, editor);
    const endBlock = closestBlock(range.endContainer, editor);
    if (startBlock && endBlock && startBlock !== endBlock) return;

    const existing = closestElement(range.commonAncestorContainer, editor)?.closest(tagName);
    if (existing && editor.contains(existing)) {
      const unwrappedRange = unwrapElement(existing);
      restoreRange(unwrappedRange);
      commitEditorDom();
      return;
    }

    const wrapper = editor.ownerDocument.createElement(tagName);
    if (range.collapsed) {
      const marker = editor.ownerDocument.createTextNode(zeroWidthSpace);
      wrapper.append(marker);
      range.insertNode(wrapper);
      const markerRange = editor.ownerDocument.createRange();
      markerRange.setStart(marker, marker.data.length);
      markerRange.collapse(true);
      restoreRange(markerRange);
      return;
    }
    wrapper.append(range.extractContents());
    range.insertNode(wrapper);
    const wrappedRange = editor.ownerDocument.createRange();
    wrappedRange.selectNodeContents(wrapper);
    restoreRange(wrappedRange);
    commitEditorDom();
  };

  const toggleBlock = (tagName: 'h2' | 'blockquote') => {
    if (locked) return;
    const editor = editorRef.current;
    const range = currentRange();
    if (!editor || !range) return;
    const selectedNode = directChildAtRange(range, editor);
    normalizeDirectInlineBlocks(editor);
    const block = closestBlock(range.startContainer, editor) ?? (selectedNode ? closestBlock(selectedNode, editor) : undefined);
    if (!block || block.tagName === 'LI') return;
    const nextTagName = block.tagName === tagName.toUpperCase() ? 'p' : tagName;
    replaceElementTag(block, nextTagName);
    editor.focus();
    refreshFormats();
    commitEditorDom();
  };

  const toggleList = () => {
    if (locked) return;
    const editor = editorRef.current;
    const range = currentRange();
    if (!editor || !range) return;
    const selectedNode = directChildAtRange(range, editor);
    normalizeDirectInlineBlocks(editor);
    const block = closestBlock(range.startContainer, editor) ?? (selectedNode ? closestBlock(selectedNode, editor) : undefined);
    if (!block) return;
    let caretTarget: HTMLElement;
    if (block.tagName === 'LI' && block.parentElement && ['UL', 'OL'].includes(block.parentElement.tagName)) {
      const list = block.parentElement;
      const paragraph = editor.ownerDocument.createElement('p');
      while (block.firstChild) paragraph.append(block.firstChild);
      const items = Array.from(list.children).filter((child): child is HTMLElement => child instanceof HTMLElement && child.tagName === 'LI');
      const itemIndex = items.indexOf(block);
      const replacements: HTMLElement[] = [];
      const listElement = (subset: HTMLElement[]) => {
        const nextList = editor.ownerDocument.createElement(list.tagName.toLowerCase());
        nextList.append(...subset);
        return nextList;
      };
      if (itemIndex > 0) replacements.push(listElement(items.slice(0, itemIndex)));
      replacements.push(paragraph);
      if (itemIndex >= 0 && itemIndex < items.length - 1) replacements.push(listElement(items.slice(itemIndex + 1)));
      list.replaceWith(...replacements);
      caretTarget = paragraph;
    } else {
      const list = editor.ownerDocument.createElement('ul');
      const item = editor.ownerDocument.createElement('li');
      while (block.firstChild) item.append(block.firstChild);
      list.append(item);
      block.replaceWith(list);
      caretTarget = item;
    }
    const caretRange = editor.ownerDocument.createRange();
    caretRange.selectNodeContents(caretTarget);
    caretRange.collapse(false);
    restoreRange(caretRange);
    commitEditorDom();
  };

  const undo = () => {
    if (locked) return;
    const history = historyRef.current;
    const previousValue = history.past.pop();
    if (previousValue === undefined) return;
    history.future.push(renderedValueRef.current ?? value);
    renderEditorValue(previousValue, true);
    onChange(previousValue);
    refreshHistoryControls();
  };

  const redo = () => {
    if (locked) return;
    const history = historyRef.current;
    const nextValue = history.future.pop();
    if (nextValue === undefined) return;
    history.past.push(renderedValueRef.current ?? value);
    renderEditorValue(nextValue, true);
    onChange(nextValue);
    refreshHistoryControls();
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (locked || !(event.metaKey || event.ctrlKey)) return;
    const key = event.key.toLocaleLowerCase();
    if (key === 'b') {
      event.preventDefault();
      toggleInline('strong');
    } else if (key === 'i') {
      event.preventDefault();
      toggleInline('em');
    } else if (key === 'k') {
      event.preventDefault();
      if (event.shiftKey) removeLink();
      else openLinkPopover();
    } else if (key === 'z') {
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
    } else if (key === 'y') {
      event.preventDefault();
      redo();
    }
  };

  const handlePaste = (event: ReactClipboardEvent<HTMLDivElement>) => {
    if (locked) return;
    event.preventDefault();
    const editor = editorRef.current;
    if (!editor) return;
    const html = event.clipboardData.getData('text/html');
    if (html.trim()) {
      const fragment = sanitizePastedHTML(editor.ownerDocument, html);
      if (fragment.hasChildNodes()) insertRichFragment(editor, fragment);
      else insertPlainText(editor, event.clipboardData.getData('text/plain'));
    } else {
      insertPlainText(editor, event.clipboardData.getData('text/plain'));
    }
    commitEditorDom();
    refreshFormats();
  };

  const handleBlur = () => {
    const editor = editorRef.current;
    if (!editor || !editor.textContent?.includes(zeroWidthSpace)) return;
    renderEditorValue(renderedValueRef.current ?? value);
  };

  const formatButtons = [
    { label: '제목', pressed: formats.heading, icon: <TitleOutlined fontSize="small" />, action: () => toggleBlock('h2') },
    { label: '굵게', pressed: formats.bold, icon: <FormatBoldOutlined fontSize="small" />, action: () => toggleInline('strong') },
    { label: '기울임', pressed: formats.italic, icon: <FormatItalicOutlined fontSize="small" />, action: () => toggleInline('em') },
    { label: '글머리 기호 목록', pressed: formats.list, icon: <FormatListBulletedOutlined fontSize="small" />, action: toggleList },
    { label: '인용', pressed: formats.quote, icon: <FormatQuoteOutlined fontSize="small" />, action: () => toggleBlock('blockquote') },
  ] as const;

  return <Field.Root controlId={controlId} required={required} disabled={disabled} readOnly={readOnly} invalid={error}>
    <Field.Header>
      <Field.Label>{label}</Field.Label>
    </Field.Header>
    <Field.Control>
      {({ labelId, messageId }) => <Box
        sx={{
          ...createInputSurfaceStyles({ variant: 'outlined', disabled, invalid: error }),
          overflow: 'hidden',
          bgcolor: locked ? designTokens.color.subtleSurface : designTokens.color.canvas,
        }}
      >
        <Stack
          role="toolbar"
          aria-label={toolbarLabel}
          direction="row"
          spacing={0.25}
          onMouseDown={(event) => event.preventDefault()}
          sx={{ minHeight: 44, px: 0.75, alignItems: 'center', borderBottom: 1, borderColor: designTokens.color.divider, flexWrap: 'wrap' }}
        >
          {formatButtons.map((button) => <Tooltip key={button.label} title={button.label}>
            <span><IconButton
              size="small"
              aria-label={button.label}
              aria-pressed={button.pressed}
              disabled={locked}
              onClick={button.action}
              sx={{
                ...toolbarIconButtonSx,
                color: button.pressed ? designTokens.color.action : designTokens.color.secondaryText,
                bgcolor: button.pressed ? designTokens.color.actionSoft : undefined,
              }}
            >{button.icon}</IconButton></span>
          </Tooltip>)}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
          <Tooltip title="링크 추가 또는 수정 (Ctrl+K)"><span><IconButton
            ref={linkButtonRef}
            size="small"
            aria-label="링크 추가 또는 수정"
            aria-haspopup="dialog"
            aria-expanded={linkPopoverOpen || undefined}
            aria-pressed={formats.link}
            disabled={locked || (!formats.link && !formats.linkable)}
            onClick={openLinkPopover}
            sx={{ ...toolbarIconButtonSx, color: formats.link ? designTokens.color.action : designTokens.color.secondaryText, bgcolor: formats.link ? designTokens.color.actionSoft : undefined }}
          ><LinkOutlined fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title="링크 해제 (Ctrl+Shift+K)"><span><IconButton
            size="small"
            aria-label="링크 해제"
            disabled={locked || !formats.link}
            onClick={removeLink}
            sx={toolbarIconButtonSx}
          ><LinkOffOutlined fontSize="small" /></IconButton></span></Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
          <Tooltip title="실행 취소"><span><IconButton size="small" aria-label="실행 취소" disabled={locked || historyRef.current.past.length === 0} onClick={undo} sx={toolbarIconButtonSx}><UndoOutlined fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title="다시 실행"><span><IconButton size="small" aria-label="다시 실행" disabled={locked || historyRef.current.future.length === 0} onClick={redo} sx={toolbarIconButtonSx}><RedoOutlined fontSize="small" /></IconButton></span></Tooltip>
        </Stack>
        <Box
          id={controlId}
          ref={editorRef}
          role="textbox"
          aria-multiline="true"
          aria-labelledby={labelId}
          aria-describedby={helperText === undefined ? undefined : messageId}
          aria-required={required || undefined}
          aria-invalid={error || undefined}
          aria-disabled={disabled || undefined}
          aria-readonly={readOnly || undefined}
          contentEditable={!locked}
          suppressContentEditableWarning
          spellCheck
          tabIndex={disabled ? -1 : 0}
          data-placeholder={placeholder}
          onInput={(_event: FormEvent<HTMLDivElement>) => commitEditorDom()}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onDrop={(event) => event.preventDefault()}
          onBlur={handleBlur}
          sx={{
            minHeight: 240,
            px: { xs: 1.5, sm: 2 },
            py: 1.75,
            color: disabled ? designTokens.color.disabledText : designTokens.color.primaryText,
            ...designTokens.typography.body,
            outline: 'none',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            '&:empty::before': {
              content: 'attr(data-placeholder)',
              color: designTokens.color.mutedText,
              pointerEvents: 'none',
            },
            '& p': { m: 0, minHeight: '1.6em' },
            '& p + p': { mt: 1 },
            '& h2': { mt: 0, mb: 1, fontSize: designTokens.typography.sectionTitle.fontSize, lineHeight: designTokens.typography.sectionTitle.lineHeight, fontWeight: 800 },
            '& ul': { my: 0.75, pl: 3 },
            '& li + li': { mt: 0.4 },
            '& blockquote': { m: 0, my: 1, pl: 1.5, borderLeft: 3, borderColor: designTokens.color.strongDivider, color: designTokens.color.secondaryText },
            '& a': { color: designTokens.color.action, textDecoration: 'underline', textUnderlineOffset: 2 },
          }}
        />
      </Box>}
    </Field.Control>
    <AnchoredPopover
      open={linkPopoverOpen}
      anchorEl={linkButtonRef.current}
      onClose={closeLinkPopover}
      padding="standard"
      ariaLabel="링크 설정"
    >
      <Box component="form" onSubmit={applyLink} noValidate>
        <Stack spacing={1.5}>
          <FormTextField
            id={linkTargetId}
            autoFocus
            label="링크 주소"
            value={linkTarget}
            onChange={(event) => { setLinkTarget(event.target.value); if (linkError) setLinkError(''); }}
            placeholder="https://example.com"
            error={Boolean(linkError)}
            helperText={linkError || 'http://, https://, mailto: 주소를 사용할 수 있습니다.'}
            slotProps={{ htmlInput: { inputMode: 'url', autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false } }}
          />
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button onClick={closeLinkPopover}>취소</Button>
            <Button type="submit" variant="contained">링크 적용</Button>
          </Stack>
        </Stack>
      </Box>
    </AnchoredPopover>
    {helperText !== undefined ? <Field.Footer><Field.Message>{helperText}</Field.Message></Field.Footer> : null}
  </Field.Root>;
}

function renderBlock(block: WorkflowRichTextBlock, index: number): ReactNode {
  if (block.type === 'heading') return <Typography key={index} component="h3" variant="h6" sx={{ mt: index === 0 ? 0 : 2, mb: 0.75 }}>{renderInline(block.children)}</Typography>;
  if (block.type === 'quote') return <Box key={index} component="blockquote" sx={{ my: 1.25, ml: 0, mr: 0, pl: 1.5, borderLeft: 3, borderColor: designTokens.color.strongDivider, color: designTokens.color.secondaryText }}><Typography component="div" variant="body2">{renderInline(block.children)}</Typography></Box>;
  if (block.type === 'list') return <Box key={index} component="ul" sx={{ my: 1, pl: 3 }}>{block.items.map((item, itemIndex) => <Typography key={itemIndex} component="li" variant="body2" sx={{ '& + &': { mt: 0.4 } }}>{renderInline(item)}</Typography>)}</Box>;
  return <Typography key={index} component="p" variant="body2" sx={{ m: 0, '& + p': { mt: 1 } }}>{renderInline(block.children)}</Typography>;
}

function renderInline(nodes: WorkflowRichTextInlineNode[]): ReactNode[] {
  return nodes.map((node, index) => {
    if (node.type === 'break') return <br key={index} />;
    if (node.type === 'strong') return <strong key={index}>{renderInline(node.children)}</strong>;
    if (node.type === 'emphasis') return <em key={index}>{renderInline(node.children)}</em>;
    if (node.type === 'link') {
      const external = node.href.startsWith('http://') || node.href.startsWith('https://');
      return <MuiLink key={index} href={node.href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}>{renderInline(node.children)}</MuiLink>;
    }
    return <span key={index}>{node.value}</span>;
  });
}

function renderMarkdownInto(editor: HTMLDivElement, value: string) {
  const ownerDocument = editor.ownerDocument;
  const fragment = ownerDocument.createDocumentFragment();
  for (const block of parseWorkflowRichTextMarkdown(value)) fragment.append(createBlockElement(ownerDocument, block));
  editor.replaceChildren(fragment);
}

function createBlockElement(ownerDocument: Document, block: WorkflowRichTextBlock): HTMLElement {
  if (block.type === 'list') {
    const list = ownerDocument.createElement('ul');
    for (const item of block.items) {
      const listItem = ownerDocument.createElement('li');
      listItem.append(createInlineFragment(ownerDocument, item));
      list.append(listItem);
    }
    return list;
  }
  const element = ownerDocument.createElement(block.type === 'heading' ? 'h2' : block.type === 'quote' ? 'blockquote' : 'p');
  element.append(createInlineFragment(ownerDocument, block.children));
  return element;
}

function createInlineFragment(ownerDocument: Document, nodes: WorkflowRichTextInlineNode[]): DocumentFragment {
  const fragment = ownerDocument.createDocumentFragment();
  for (const node of nodes) {
    if (node.type === 'text') fragment.append(ownerDocument.createTextNode(node.value));
    else if (node.type === 'break') fragment.append(ownerDocument.createElement('br'));
    else {
      const element = ownerDocument.createElement(node.type === 'strong' ? 'strong' : node.type === 'emphasis' ? 'em' : 'a');
      if (node.type === 'link') element.setAttribute('href', node.href);
      element.append(createInlineFragment(ownerDocument, node.children));
      fragment.append(element);
    }
  }
  return fragment;
}

function serializeEditor(editor: HTMLDivElement): string {
  const blocks: string[] = [];
  let pendingInline = '';
  const flushPendingInline = () => {
    const value = normalizeSerializedBlock(pendingInline);
    if (value) blocks.push(value);
    pendingInline = '';
  };
  for (const node of Array.from(editor.childNodes)) {
    if (node.nodeType === 3) {
      pendingInline += serializeInlineNode(node);
      continue;
    }
    const element = node as HTMLElement;
    if (['UL', 'OL'].includes(element.tagName)) {
      flushPendingInline();
      const items = Array.from(element.children)
        .filter((child) => child.tagName === 'LI')
        .map((child) => `- ${normalizeSerializedBlock(serializeInlineChildren(child))}`)
        .filter((item) => item !== '- ');
      if (items.length) blocks.push(items.join('\n'));
    } else if (element.tagName === 'BLOCKQUOTE') {
      flushPendingInline();
      const quote = normalizeSerializedBlock(serializeInlineChildren(element));
      if (quote) blocks.push(quote.split('\n').map((line) => `> ${line}`).join('\n'));
    } else if (/^H[1-3]$/.test(element.tagName)) {
      flushPendingInline();
      const heading = normalizeSerializedBlock(serializeInlineChildren(element));
      if (heading) blocks.push(`## ${heading}`);
    } else if (blockTags.has(element.tagName)) {
      flushPendingInline();
      const block = normalizeSerializedBlock(serializeInlineChildren(element));
      if (block) blocks.push(block);
    } else {
      pendingInline += serializeInlineNode(element);
    }
  }
  flushPendingInline();
  return blocks.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
}

function serializeInlineChildren(node: Node): string {
  return Array.from(node.childNodes).map(serializeInlineNode).join('');
}

function serializeInlineNode(node: Node): string {
  if (node.nodeType === 3) return escapeWorkflowRichTextMarkdown((node.textContent ?? '').replaceAll(zeroWidthSpace, ''));
  const element = node as HTMLElement;
  if (element.tagName === 'BR') return '\n';
  const content = serializeInlineChildren(element);
  if (!content) return '';
  if (element.tagName === 'STRONG' || element.tagName === 'B') return `**${content}**`;
  if (element.tagName === 'EM' || element.tagName === 'I') return `*${content}*`;
  if (element.tagName === 'A') {
    const target = normalizeWorkflowRichTextLinkTarget(element.getAttribute('href') ?? '');
    return target ? `[${content}](${escapeWorkflowRichTextLinkTarget(target)})` : content;
  }
  return content;
}

function normalizeSerializedBlock(value: string): string {
  return value.split('\n').map((line) => line.trimEnd()).join('\n').trim();
}

function selectionInside(editor: HTMLElement, selection: Selection): boolean {
  return Boolean(selection.anchorNode && selection.focusNode && editor.contains(selection.anchorNode) && editor.contains(selection.focusNode));
}

function closestElement(node: Node, editor: HTMLElement): Element | null {
  const element = node.nodeType === 1 ? node as Element : node.parentElement;
  return element && editor.contains(element) ? element : null;
}

function closestBlock(node: Node, editor: HTMLElement): HTMLElement | undefined {
  let element = closestElement(node, editor) as HTMLElement | null;
  while (element && element !== editor) {
    if (blockTags.has(element.tagName)) return element;
    element = element.parentElement;
  }
  return undefined;
}

function directChildAtRange(range: Range, editor: HTMLElement): Node | undefined {
  if (range.startContainer === editor) {
    const nodeCount = editor.childNodes.length;
    if (nodeCount === 0) return undefined;
    return editor.childNodes[Math.min(Math.max(range.startOffset - 1, 0), nodeCount - 1)];
  }
  let node: Node | null = range.startContainer;
  while (node?.parentNode && node.parentNode !== editor) node = node.parentNode;
  return node?.parentNode === editor ? node : undefined;
}

/**
 * contenteditable은 빈 editor에서 바로 타이핑하면 문단 없이 text/inline node를 최상위에 둘 수 있다.
 * block 서식을 적용하기 전에 이런 연속 inline node를 문단으로 정규화해 실제 키보드 입력과
 * Markdown에서 다시 렌더된 DOM이 같은 행동을 하게 한다.
 */
function normalizeDirectInlineBlocks(editor: HTMLElement) {
  let paragraph: HTMLParagraphElement | undefined;
  for (const node of Array.from(editor.childNodes)) {
    const element = node.nodeType === 1 ? node as HTMLElement : undefined;
    const block = element && (blockTags.has(element.tagName) || element.tagName === 'UL' || element.tagName === 'OL');
    if (block) {
      paragraph = undefined;
      continue;
    }
    if (!paragraph) {
      paragraph = editor.ownerDocument.createElement('p');
      editor.insertBefore(paragraph, node);
    }
    paragraph.append(node);
  }
}

function unwrapElement(element: Element): Range {
  const ownerDocument = element.ownerDocument;
  const parent = element.parentNode!;
  const first = element.firstChild;
  const last = element.lastChild;
  while (element.firstChild) parent.insertBefore(element.firstChild, element);
  element.remove();
  const range = ownerDocument.createRange();
  if (first && last) {
    range.setStartBefore(first);
    range.setEndAfter(last);
  } else {
    range.selectNodeContents(parent);
    range.collapse(false);
  }
  return range;
}

function replaceElementTag(element: HTMLElement, tagName: string): HTMLElement {
  const replacement = element.ownerDocument.createElement(tagName);
  while (element.firstChild) replacement.append(element.firstChild);
  element.replaceWith(replacement);
  return replacement;
}

function placeCaretAtEnd(editor: HTMLElement) {
  const range = editor.ownerDocument.createRange();
  range.selectNodeContents(editor);
  range.collapse(false);
  const selection = editor.ownerDocument.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
  editor.focus();
}

const discardedPasteTags = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'TEMPLATE', 'META', 'LINK', 'HEAD']);

function sanitizePastedHTML(ownerDocument: Document, html: string): DocumentFragment {
  const template = ownerDocument.createElement('template');
  template.innerHTML = html;
  const fragment = ownerDocument.createDocumentFragment();
  for (const node of Array.from(template.content.childNodes)) fragment.append(...sanitizePastedNode(ownerDocument, node));
  return fragment;
}

function sanitizePastedNode(ownerDocument: Document, node: Node): Node[] {
  if (node.nodeType === Node.TEXT_NODE) return [ownerDocument.createTextNode(node.textContent ?? '')];
  if (node.nodeType !== Node.ELEMENT_NODE) return [];
  const source = node as HTMLElement;
  if (discardedPasteTags.has(source.tagName)) return [];

  const children = () => Array.from(source.childNodes).flatMap((child) => sanitizePastedNode(ownerDocument, child));
  if (source.tagName === 'BR') return [ownerDocument.createElement('br')];
  if (source.tagName === 'A') {
    const linkChildren = children();
    const target = normalizeWorkflowRichTextLinkTarget(source.getAttribute('href') ?? '');
    if (!target) return linkChildren;
    const link = ownerDocument.createElement('a');
    link.setAttribute('href', target);
    link.append(...linkChildren);
    return [link];
  }
  if (source.tagName === 'STRONG' || source.tagName === 'B') {
    const strong = ownerDocument.createElement('strong');
    strong.append(...children());
    return [strong];
  }
  if (source.tagName === 'EM' || source.tagName === 'I') {
    const emphasis = ownerDocument.createElement('em');
    emphasis.append(...children());
    return [emphasis];
  }
  if (/^H[1-6]$/.test(source.tagName)) {
    const heading = ownerDocument.createElement('h2');
    heading.append(...flattenPastedBlockChildren(ownerDocument, children()));
    return [heading];
  }
  if (source.tagName === 'BLOCKQUOTE') {
    const quote = ownerDocument.createElement('blockquote');
    quote.append(...flattenPastedBlockChildren(ownerDocument, children()));
    return [quote];
  }
  if (source.tagName === 'UL' || source.tagName === 'OL') {
    const list = ownerDocument.createElement('ul');
    const items = children();
    for (const item of items) {
      if (item instanceof HTMLElement && item.tagName === 'LI') list.append(item);
      else if (item.textContent?.trim()) {
        const listItem = ownerDocument.createElement('li');
        listItem.append(item);
        list.append(listItem);
      }
    }
    return list.hasChildNodes() ? [list] : [];
  }
  if (source.tagName === 'LI') {
    const item = ownerDocument.createElement('li');
    item.append(...flattenPastedBlockChildren(ownerDocument, children()));
    return [item];
  }
  if (['P', 'DIV', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'PRE'].includes(source.tagName)) {
    const sanitizedChildren = children();
    if (sanitizedChildren.some(isEditorBlockNode)) return sanitizedChildren;
    const paragraph = ownerDocument.createElement('p');
    paragraph.append(...sanitizedChildren);
    return paragraph.hasChildNodes() ? [paragraph] : [];
  }
  if (source.tagName === 'IMG') {
    const alt = source.getAttribute('alt')?.trim();
    return alt ? [ownerDocument.createTextNode(alt)] : [];
  }
  return children();
}

function flattenPastedBlockChildren(ownerDocument: Document, nodes: Node[]): Node[] {
  const flattened: Node[] = [];
  for (const node of nodes) {
    if (!isEditorBlockNode(node)) {
      flattened.push(node);
      continue;
    }
    if (flattened.length && flattened[flattened.length - 1].nodeName !== 'BR') flattened.push(ownerDocument.createElement('br'));
    flattened.push(...Array.from(node.childNodes));
  }
  return flattened;
}

function isEditorBlockNode(node: Node): boolean {
  return node.nodeType === Node.ELEMENT_NODE && (blockTags.has((node as HTMLElement).tagName) || ['UL', 'OL'].includes((node as HTMLElement).tagName));
}

function insertRichFragment(editor: HTMLElement, fragment: DocumentFragment) {
  const selection = editor.ownerDocument.getSelection();
  const range = selection?.rangeCount && selectionInside(editor, selection) ? selection.getRangeAt(0) : editor.ownerDocument.createRange();
  if (!selection?.rangeCount || !selectionInside(editor, selection)) {
    range.selectNodeContents(editor);
    range.collapse(false);
  }
  const insertedNodes = Array.from(fragment.childNodes);
  if (!insertedNodes.length) return;
  const hasBlocks = insertedNodes.some(isEditorBlockNode);
  const startBlock = closestBlock(range.startContainer, editor);
  const endBlock = closestBlock(range.endContainer, editor);

  if (hasBlocks && startBlock && startBlock === endBlock && startBlock.parentElement === editor && startBlock.tagName !== 'LI') {
    const beforeRange = editor.ownerDocument.createRange();
    beforeRange.selectNodeContents(startBlock);
    beforeRange.setEnd(range.startContainer, range.startOffset);
    const afterRange = editor.ownerDocument.createRange();
    afterRange.selectNodeContents(startBlock);
    afterRange.setStart(range.endContainer, range.endOffset);
    const before = beforeRange.cloneContents();
    const after = afterRange.cloneContents();
    const replacements: Node[] = [];
    if (fragmentHasContent(before)) {
      const beforeBlock = startBlock.cloneNode(false) as HTMLElement;
      beforeBlock.append(before);
      replacements.push(beforeBlock);
    }
    replacements.push(...insertedNodes);
    if (fragmentHasContent(after)) {
      const afterBlock = startBlock.cloneNode(false) as HTMLElement;
      afterBlock.append(after);
      replacements.push(afterBlock);
    }
    startBlock.replaceWith(...replacements);
    setCaretAfterNode(editor, insertedNodes[insertedNodes.length - 1]);
    return;
  }

  range.deleteContents();
  range.insertNode(fragment);
  setCaretAfterNode(editor, insertedNodes[insertedNodes.length - 1]);
}

function fragmentHasContent(fragment: DocumentFragment): boolean {
  return Boolean(fragment.textContent?.trim() || fragment.querySelector('br'));
}

function setCaretAfterNode(editor: HTMLElement, node: Node) {
  const selection = editor.ownerDocument.getSelection();
  const range = editor.ownerDocument.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function insertPlainText(editor: HTMLElement, value: string) {
  const selection = editor.ownerDocument.getSelection();
  const range = selection?.rangeCount && selectionInside(editor, selection) ? selection.getRangeAt(0) : editor.ownerDocument.createRange();
  if (!selection?.rangeCount || !selectionInside(editor, selection)) {
    range.selectNodeContents(editor);
    range.collapse(false);
  }
  range.deleteContents();
  const fragment = editor.ownerDocument.createDocumentFragment();
  const lines = value.replace(/\r\n?/g, '\n').split('\n');
  let lastNode: Node | undefined;
  lines.forEach((line, index) => {
    if (index > 0) {
      lastNode = editor.ownerDocument.createElement('br');
      fragment.append(lastNode);
    }
    if (line) {
      lastNode = editor.ownerDocument.createTextNode(line);
      fragment.append(lastNode);
    }
  });
  range.insertNode(fragment);
  if (lastNode) {
    range.setStartAfter(lastNode);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
}
